import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import StartScreen from "./pages/StartScreen";
import MainScreen from "./pages/MainScreen";
import DashboardLayout from "./pages/DashboardLayout";
import ProjectsScreen from "./pages/ProjectsScreen";
import ProjectDetailsScreen from "./pages/ProjectDetailsScreen";
import ProjectManagementScreen from "./pages/ProjectManagementScreen";
import ServicesScreen from "./pages/ServicesScreen";
import MembersScreen from "./pages/MembersScreen";
import ProfileScreen from "./pages/ProfileScreen";
import AdminScreen from "./pages/AdminScreen";
import {
  ensureDevAuth,
  getEffectiveAccessLevel,
  getJwt,
  getProfile,
  isAdmin,
  subscribeAuthChange,
} from "./utils/auth";

ensureDevAuth();
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

function buildPresenceWebSocketURL(token) {
  if (!token || typeof window === "undefined") {
    return "";
  }

  try {
    const base = new URL(API_BASE, window.location.origin);
    base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
    base.pathname = "/ws";
    base.search = "";
    base.searchParams.set("token", token);
    return base.toString();
  } catch (error) {
    return "";
  }
}

function App() {
  const [, setAuthTick] = useState(0);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const jwt = getJwt();
  const profile = getProfile();
  const isAuthorized = Boolean(jwt && profile);
  const isAdminUser = isAdmin(getEffectiveAccessLevel(profile));

  useEffect(() => {
    const handleAuthChange = () => setAuthTick((prev) => prev + 1);
    const unsubscribe = subscribeAuthChange(handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    return () => {
      unsubscribe();
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const telegramID = String(profile?.telegram_id || "").trim();

    const clearReconnect = () => {
      if (!reconnectRef.current) {
        return;
      }
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    };

    const closeSocket = () => {
      const current = wsRef.current;
      wsRef.current = null;
      if (!current) {
        return;
      }
      if (
        current.readyState === WebSocket.OPEN ||
        current.readyState === WebSocket.CONNECTING
      ) {
        current.close();
      }
    };

    if (!isAuthorized || !telegramID || !jwt) {
      clearReconnect();
      reconnectAttemptsRef.current = 0;
      closeSocket();
      return;
    }

    let disposed = false;

    const connect = () => {
      if (disposed || wsRef.current) {
        return;
      }

      const wsURL = buildPresenceWebSocketURL(jwt);
      if (!wsURL) {
        return;
      }

      const socket = new WebSocket(wsURL);
      wsRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onclose = () => {
        if (wsRef.current === socket) {
          wsRef.current = null;
        }
        if (disposed) {
          return;
        }
        const retryDelayMs = Math.min(
          30000,
          1000 * 2 ** reconnectAttemptsRef.current
        );
        reconnectAttemptsRef.current += 1;
        reconnectRef.current = setTimeout(() => {
          reconnectRef.current = null;
          connect();
        }, retryDelayMs);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnect();
      closeSocket();
    };
  }, [isAuthorized, jwt, profile?.telegram_id]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            isAuthorized ? <Navigate to="/profile" replace /> : <StartScreen />
          }
        />
        <Route
          element={
            isAuthorized ? <DashboardLayout /> : <Navigate to="/" replace />
          }
        >
          <Route path="/main" element={<MainScreen />} />
          <Route path="/projects" element={<ProjectsScreen />} />
          <Route path="/projects/:id" element={<ProjectDetailsScreen />} />
          <Route
            path="/projects/:id/manage"
            element={<ProjectManagementScreen />}
          />
          <Route path="/services" element={<ServicesScreen />} />
          <Route path="/members" element={<MembersScreen />} />
          <Route
            path="/admin"
            element={isAdminUser ? <AdminScreen /> : <Navigate to="/main" replace />}
          />
          <Route path="/metrics" element={<Navigate to={isAdminUser ? "/admin" : "/main"} replace />} />
          <Route path="/profile" element={<ProfileScreen />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
